import { isObject } from "lodash-es";
import React, { useMemo } from "react";
import { useParams } from "react-router";
import { CodeBlock } from "../../../components/CodeBlock";
import { formatDateTime, humanizeString } from "../../../util/formatters";
import { isValidJSON } from "../../../util/helpers";
import { SystemConfigLayout } from "../../components/SystemConfigLayout";
import { Routes } from "../../Routes";
import { useAdminResource } from "../../utils";

const useBreadcrumbs = (pageTitle, caseType, benefitId) =>
  useMemo(
    () => [
      { url: Routes.pullBenefitsLogs.url(), label: Routes.pullBenefitsLogs.label },
      { url: Routes.benefitsDetail.url(caseType, benefitId), label: pageTitle, active: true },
    ],
    [pageTitle, caseType, benefitId]
  );

const useBenefit = (source, id) => useAdminResource(`/admin/benefits_log/${source}/${id}.json`);
const displayValue = (value) => (value ? humanizeString(value) : "—");
const formatCode = (value) => (isValidJSON(value) || isObject(value) ? JSON.stringify(value, null, 2) : value);

const ProviderTransaction = ({ transaction, index }) => (
  <section className="mb-5">
    <h2 className="h5">Provider transaction {index + 1}</h2>
    <dl className="row">
      <dt className="col-sm-3">Provider</dt>
      <dd className="col-sm-9">{displayValue(transaction.provider || transaction.source)}</dd>
      <dt className="col-sm-3">Transaction ID</dt>
      <dd className="col-sm-9">{transaction.source_transaction_id || "—"}</dd>
      <dt className="col-sm-3">Status Code</dt>
      <dd className="col-sm-9">{transaction.status_code || "—"}</dd>
      {transaction.provider === "pnt" && (
        <>
          <dt className="col-sm-3">Payor Profile</dt>
          <dd className="col-sm-9">{displayValue(transaction.payor_profile)}</dd>
          <dt className="col-sm-3">Payor ID</dt>
          <dd className="col-sm-9">{transaction.payor_id || "—"}</dd>
          <dt className="col-sm-3">Response Type</dt>
          <dd className="col-sm-9">{displayValue(transaction.response_type)}</dd>
          <dt className="col-sm-3">Provider Codes</dt>
          <dd className="col-sm-9">{transaction.provider_codes?.join(", ") || "—"}</dd>
        </>
      )}
      <dt className="col-sm-3">Submitted</dt>
      <dd className="col-sm-9">{formatDateTime(transaction.created_at, "—")}</dd>
      <dt className="col-sm-3">Request</dt>
      <dd className="col-sm-9">
        <CodeBlock code={formatCode(transaction.response_body?.payload)} />
      </dd>
      <dt className="col-sm-3">Response</dt>
      <dd className="col-sm-9">
        <CodeBlock code={formatCode(transaction.response_body?.result)} />
      </dd>
    </dl>
  </section>
);

export const PullBenefitsLogDetailPage = () => {
  const { caseType, benefitId } = useParams();
  const { resource: benefitData, isLoading } = useBenefit(caseType, benefitId);
  const pageTitle = `Benefit #${benefitId}`;
  const breadcrumbs = useBreadcrumbs(pageTitle, caseType, benefitId);
  const providerTransactions = benefitData?.provider_transactions || [];

  return (
    <SystemConfigLayout title={pageTitle} showBreadcrumbs crumbs={breadcrumbs} loading={isLoading}>
      <dl className="row">
        <dt className="col-sm-3">Source</dt>
        <dd className="col-sm-9">{benefitData?.source_label || displayValue(benefitData?.source)}</dd>
        <dt className="col-sm-3">Provider</dt>
        <dd className="col-sm-9">{displayValue(benefitData?.provider)}</dd>
        <dt className="col-sm-3">State</dt>
        <dd className="col-sm-9">{displayValue(benefitData?.state)}</dd>
        <dt className="col-sm-3">Status Code</dt>
        <dd className="col-sm-9">{benefitData?.status_code ?? "—"}</dd>
        <dt className="col-sm-3">Transaction ID</dt>
        <dd className="col-sm-9">{benefitData?.source_transaction_id || "—"}</dd>
        <dt className="col-sm-3">Outcome</dt>
        <dd className="col-sm-9">{displayValue(benefitData?.outcome || benefitData?.error_category)}</dd>
        <dt className="col-sm-3">Business Attempt</dt>
        <dd className="col-sm-9">{benefitData?.follow_up_number || "—"}</dd>
        <dt className="col-sm-3">Polls</dt>
        <dd className="col-sm-9">{benefitData?.poll_count ?? "—"}</dd>
        <dt className="col-sm-3">Provider Failures</dt>
        <dd className="col-sm-9">{benefitData?.provider_error_attempt_count ?? "—"}</dd>
        <dt className="col-sm-3">Started</dt>
        <dd className="col-sm-9">{formatDateTime(benefitData?.started_at, "—")}</dd>
        <dt className="col-sm-3">Completed</dt>
        <dd className="col-sm-9">{formatDateTime(benefitData?.completed_at, "—")}</dd>
      </dl>

      {benefitData?.validation_errors?.length > 0 && (
        <section className="mb-5">
          <h2 className="h5">Validation Errors</h2>
          <CodeBlock code={formatCode(benefitData.validation_errors)} />
        </section>
      )}

      {providerTransactions.length > 0 ? (
        providerTransactions.map((transaction, index) => (
          <ProviderTransaction key={`${transaction.source}-${transaction.id}`} transaction={transaction} index={index} />
        ))
      ) : (
        <section>
          <h2 className="h5">Response Body</h2>
          <CodeBlock code={formatCode(benefitData?.response_body)} />
        </section>
      )}
    </SystemConfigLayout>
  );
};
